import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 data-[loading=true]:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        static:
          "border-transparent bg-secondary text-secondary-foreground",
        tag:
          "shrink-0 border-transparent rounded-xs bg-dinnerplate-white px-2 font-inter leading-tight-sm text-grey",
        status:
          "border-transparent font-inter font-semibold leading-tight-sm text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type StatusTone = "neutral" | "yellow" | "mint" | "red" | "blue" | "cinnamon" | "plum"

const statusToneVariants = cva("", {
  variants: {
    tone: {
      neutral: "bg-dinnerplate-white text-grey",
      yellow: "bg-custard-yellow text-turmeric-yellow",
      mint: "bg-yield-positive-bg text-confirmed-green",
      red: "bg-chilli-blush text-chilli-red",
      blue: "bg-blue-pea-lighter text-blue-pea",
      cinnamon: "bg-cinnamon-brown-light text-walnut-brown",
      plum: "bg-plum-lighter text-plum",
    },
  },
})

const statusSizeVariants = cva("rounded-xs", {
  variants: {
    size: {
      sm: "px-2 py-1 gap-1.5",
      md: "w-status-badge pl-4 pr-5 py-2 gap-2 justify-center border",
    },
  },
})

const STATUS_TONE_BORDERS: Record<StatusTone, string> = {
  neutral: "border-grey-light",
  yellow: "border-butter-yellow",
  mint: "border-yield-positive-border",
  red: "border-chilli-red-light",
  blue: "border-blue-pea-light",
  cinnamon: "border-cinnamon-brown",
  plum: "border-plum-light",
}

// At md size the neutral badge sits on the lighter dinnerplate surface
// (Figma Inactive = dinnerplate-white-light), whereas the sm Outbound tag uses
// the darker dinnerplate-white. Only neutral differs by size; the other tones
// share one background across both sizes, so they need no md override here.
const STATUS_MD_TONE_BACKGROUNDS: Partial<Record<StatusTone, string>> = {
  neutral: "bg-dinnerplate-white-light",
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  tone?: StatusTone
  size?: "sm" | "md"
  icon?: React.ReactNode
  bordered?: boolean
}

function Badge({
  className,
  variant,
  tone,
  size,
  icon,
  bordered = false,
  children,
  ...props
}: BadgeProps) {
  const isStatus = variant === "status"
  const statusTone = tone ?? "neutral"
  const statusSize = size ?? "sm"

  const statusClasses = isStatus
    ? cn(
        statusToneVariants({ tone: statusTone }),
        statusSizeVariants({ size: statusSize }),
        (statusSize === "md" || bordered) && STATUS_TONE_BORDERS[statusTone],
        statusSize === "md" && STATUS_MD_TONE_BACKGROUNDS[statusTone]
      )
    : ""

  return (
    <div
      className={cn(badgeVariants({ variant }), statusClasses, className)}
      {...props}
    >
      {isStatus && icon ? (
        <span className="inline-flex size-4 items-center justify-center shrink-0 [&>svg]:size-full">
          {icon}
        </span>
      ) : null}
      {children}
    </div>
  )
}

export { Badge, badgeVariants }
